'use client';

import { useState, useEffect, RefObject } from 'react';
import { Page } from '../types';

// Layout constants
const LOGO_HEIGHT = 56; // h-14
const LOGO_GAP = 24; // gap-6 between logo and nav items
const NAV_ITEM_HEIGHT = 56; // 40px icon + 12px label + gap
const NAV_ITEM_GAP = 16; // gap-4 between nav items
const NAV_PADDING = 16; // py-2 = 8px top + 8px bottom

interface UseOverflowNavOptions {
  onResize?: () => void;
}

interface UseOverflowNavResult {
  visiblePages: Page[];
  overflowPages: Page[];
}

export function useOverflowNav(
  pages: Page[],
  parentContainerRef: RefObject<HTMLElement | null>,
  options?: UseOverflowNavOptions
): UseOverflowNavResult {
  const [visibleCount, setVisibleCount] = useState(pages.length);

  useEffect(() => {
    const container = parentContainerRef.current;
    if (!container) return;

    const calculateVisibleItems = () => {
      const containerHeight = container.clientHeight;

      // Available height for nav items = container - logo - gap - padding
      const availableForNav = containerHeight - LOGO_HEIGHT - LOGO_GAP - NAV_PADDING;

      // Height needed for n items: n * NAV_ITEM_HEIGHT + (n-1) * NAV_ITEM_GAP
      const heightForAllItems =
        pages.length * NAV_ITEM_HEIGHT + (pages.length - 1) * NAV_ITEM_GAP;

      // Always reserve space for the More button (it's permanently visible)
      const heightForAllItemsPlusMore =
        heightForAllItems + NAV_ITEM_HEIGHT + NAV_ITEM_GAP;

      if (heightForAllItemsPlusMore <= availableForNav) {
        // All items + More button fit
        setVisibleCount(pages.length);
      } else {
        // Need More button - it takes same space as a nav item
        // Available for visible items = availableForNav - More button - gap before it
        const availableForVisible = availableForNav - NAV_ITEM_HEIGHT - NAV_ITEM_GAP;

        // Solve: n * NAV_ITEM_HEIGHT + (n-1) * NAV_ITEM_GAP <= availableForVisible
        // n * (NAV_ITEM_HEIGHT + NAV_ITEM_GAP) - NAV_ITEM_GAP <= availableForVisible
        // n <= (availableForVisible + NAV_ITEM_GAP) / (NAV_ITEM_HEIGHT + NAV_ITEM_GAP)
        const maxItems = Math.floor(
          (availableForVisible + NAV_ITEM_GAP) / (NAV_ITEM_HEIGHT + NAV_ITEM_GAP)
        );
        setVisibleCount(Math.max(0, maxItems));
      }
    };

    const resizeObserver = new ResizeObserver(() => {
      calculateVisibleItems();
      options?.onResize?.();
    });
    resizeObserver.observe(container);
    calculateVisibleItems(); // Initial calculation

    return () => resizeObserver.disconnect();
  }, [pages.length, parentContainerRef, options]);

  return {
    visiblePages: pages.slice(0, visibleCount),
    overflowPages: pages.slice(visibleCount),
  };
}
